import { isErrorFromAlias, Zodios, type ZodiosBodyByAlias, type ZodiosInstance } from "@zodios/core";
import type { z } from "zod";

import type { ModuleOpenUser, moduleSchema, UpdateInteractModule } from "@skilitics/nautilus-endpoint-definition";
import { api } from "@skilitics/nautilus-endpoint-definition";

import { attachAuthorizationMiddleware } from "../../../api/index.ts";
import { silenceError } from "../../../errors/silenceError.ts";
import { getRequiredAppSetting } from "../../../shared/index.ts";

type Api = ZodiosInstance<typeof api>;

export interface UpdateSessionBody {
  lockSeconds?: number;
  // todo: better name? Take over existing session, but currently only for your own sessions
  force?: boolean;
}

// this BS is because of axios types leaking through zodios. copilot wrote 95% of this code
export interface ModuleStoreApi {
  get: (moduleId: string) => Promise<Awaited<ReturnType<Api["getInteractModule"]>>>;
  getAll: (
    queries: NonNullable<Parameters<Api["getInteractModules"]>[0]>["queries"],
  ) => ReturnType<Api["getInteractModules"]>;
  updateSession: (moduleId: string, sessionId: string, body?: UpdateSessionBody) => Promise<void>;
  add: (module: z.infer<typeof moduleSchema>) => ReturnType<Api["createInteractModule"]>;
  update: (module: UpdateInteractModule) => ReturnType<Api["putInteractModule"]>;
  remove: (moduleId: string) => ReturnType<Api["postInteractModuleAction"]>;
  export: (moduleId: string) => ReturnType<Api["exportModule"]>;
  import: (module: ZodiosBodyByAlias<typeof api, "importModule">) => ReturnType<Api["importModule"]>;
}

const endpoint = getRequiredAppSetting(import.meta.env.REACT_APP_INTERACT_ENDPOINT, "INTERACT_ENDPOINT");

const apiClient = new Zodios(endpoint, api);

attachAuthorizationMiddleware(apiClient.axios);

export class ModuleSessionConflict extends Error {
  name = "ModuleSessionConflict";
  openUser: ModuleOpenUser | null;

  constructor(message: string = "Module already in use", openUser: ModuleOpenUser | null = null) {
    super(message);
    this.openUser = openUser;
  }
}

export const moduleStoreApi: ModuleStoreApi = {
  get: async (moduleId) => {
    try {
      return await apiClient.getInteractModule({ params: { moduleId } });
    } catch (error) {
      // Don't show toasts for e.g. not found errors
      // We still want to throw so the different causes can be distinguished (at least in the future).
      if (isErrorFromAlias(api, "getInteractModule", error)) {
        silenceError(error);
      }
      throw error;
    }
  },
  getAll: async (queries) => await apiClient.getInteractModules({ queries }),
  updateSession: async (moduleId, sessionId, body = {}) => {
    try {
      return await apiClient.postInteractModuleSession(body, { params: { moduleId, sessionId } });
    } catch (error) {
      if (isErrorFromAlias(api, "postInteractModuleSession", error)) {
        if (error.response.status === 409) {
          const { message, openUser } = error.response.data;
          throw new ModuleSessionConflict(message, openUser);
        }
      }
      throw error;
    }
  },
  add: async (module) => await apiClient.createInteractModule(module),
  update: async (module) => await apiClient.putInteractModule(module, { params: { moduleId: module.id } }),
  remove: async (moduleId) =>
    await apiClient.postInteractModuleAction({ action: { type: "soft-delete" } }, { params: { moduleId } }),
  export: async (moduleId) => await apiClient.exportModule({ params: { hostId: moduleId } }),
  import: async (module) => await apiClient.importModule(module),
};

export const useModuleStore = (): ModuleStoreApi => moduleStoreApi;
